/**
 * Continuous session recording — the widget's browser half.
 *
 * The TypeScript twin of the consoles' webui/static/shared/recordingSession.js
 * (voice only: no video, no compositor). Nothing is shared at runtime — the
 * widget is its own bundle on other people's pages — so the logic is mirrored
 * here and kept in step by test/recorder.test.ts, which carries the consoles'
 * tests/js/recording_session.test.mjs case for case. The server half is
 * deutero_interviews/api/recording_sessions.py; the shape of a finished
 * session is the dashboard's docs/recording-media-contract.md.
 *
 * One `Recorder` per interview. It owns:
 *   - the AudioWorklet (src/worklets.ts RECORDER_WORKLET_SOURCE), loaded once
 *     and running until the widget goes: input 0 the participant's mic (the
 *     widget's mic gate), input 1 the interviewer's TTS tap (before the
 *     widget's output gain, so muted interviewer audio is still recorded);
 *   - a PREROLL_S ring buffer per side, so a session started by activity keeps
 *     the words that started it;
 *   - the idle timer, and the current `RecordingSession` (or none: "paused").
 *
 * Clocks. The worklet's page clock counts frames from its first process() and
 * never resets; a session's `origin` is the page frame of its sample 0.
 * `captureFrame()` is the render/ingest side — use it for anything about the
 * MIC (0x04 mic frames, releases); `pageFrame()` the output side (what the
 * participant hears now), for interviewer playout only. Mixing them shifts a
 * bleep window early by the output latency.
 *
 * A session ends on the first of: no activity for RECORDING_IDLE_END_S (the
 * next activity starts seq + 1), pagehide (keepalive end), the interview
 * completing (end("complete")), or RECORDING_MAX_SESSION_S of audio (rolled
 * over at that exact frame, so sessions lie end to end).
 *
 * Each side is cut into CHUNK_S mono PCM16 chunks at exact multiples of the
 * chunk length (session samples) and PUT as
 * …/recording-sessions/{sid}/audio/{p|i}/{start_sample}. In a masking study
 * participant chunks pass through a HoldBack: they leave the browser only once
 * every sample is released (the server's `rec_clear`), HOLD_BACK_S after they
 * closed, or when the session stops (end, pagehide, cap rollover) — then with
 * every unreleased sample and bleep window zeroed, never raw.
 */
import { loadWorklet, RECORDER_WORKLET_SOURCE } from "./worklets";

export const RECORDING_IDLE_END_S = 180;
export const RECORDING_MAX_SESSION_S = 5400;
export const PREROLL_S = 4;
export const CHUNK_S = 4;
export const HOLD_BACK_S = 120;
/** Timing events are batched into one PUT this often. */
export const EVENT_FLUSH_MS = 5000;

const RETRY_DELAYS_MS = [1000, 3000, 9000];
const STOP_WAIT_MS = 5000;
// fetch keepalive allows 64 KB in flight per page, all requests together.
const KEEPALIVE_BUDGET = 64 * 1024;
const END_RESERVE = 2 * 1024;

export type Side = "p" | "i";
export type Span = [number, number];
export interface Chunk {
  side: Side;
  start: number;
  pcm: Int16Array;
}
export type FetchFn = (url: string, init: RequestInit) => Promise<Response>;
export type HeadersFn = (extra?: Record<string, string>) => Record<string, string>;
export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const realTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};
const realFetch: FetchFn = (url, init) => globalThis.fetch(url, init);
const noHeaders: HeadersFn = (extra = {}) => ({ ...extra });

/** Float in [-1, 1] → PCM16 (the Int16Array store truncates toward zero). */
function toInt16(v: number): number {
  const s = v > 1 ? 1 : v < -1 ? -1 : v || 0;
  return s < 0 ? s * 0x8000 : s * 0x7fff;
}

const bytesOf = (pcm: Int16Array) =>
  new Uint8Array(pcm.buffer as ArrayBuffer, pcm.byteOffset, pcm.byteLength);

// ── Chunker: per side, cut at exact multiples of sampleRate*seconds ───────

interface Open {
  start: number;
  fill: number;
  buf: Int16Array;
}

export class Chunker {
  readonly size: number;
  private sides = new Map<Side, Open>();

  constructor(sampleRate: number, seconds = CHUNK_S) {
    this.size = Math.max(1, Math.round(sampleRate * seconds));
  }

  private open(start: number): Open {
    const into = ((start % this.size) + this.size) % this.size;
    return { start, fill: 0, buf: new Int16Array(this.size - into) };
  }

  /** `frame` is the (session) sample of samples[0]. */
  push(side: Side, frame: number, samples: Float32Array): Chunk[] {
    const out: Chunk[] = [];
    let st = this.sides.get(side) ?? null;
    if (st && st.start + st.fill !== frame) {
      // Not contiguous (the worklet never skips): close what we have rather
      // than misplace samples.
      if (st.fill) out.push({ side, start: st.start, pcm: st.buf.slice(0, st.fill) });
      st = null;
    }
    if (!st) {
      st = this.open(frame);
      this.sides.set(side, st);
    }
    let k = 0;
    while (k < samples.length) {
      const take = Math.min(st.buf.length - st.fill, samples.length - k);
      for (let j = 0; j < take; j++) st.buf[st.fill + j] = toInt16(samples[k + j]!);
      st.fill += take;
      k += take;
      if (st.fill === st.buf.length) {
        out.push({ side, start: st.start, pcm: st.buf });
        st = this.open(st.start + st.fill);
        this.sides.set(side, st);
      }
    }
    return out;
  }

  flush(): Chunk[] {
    const out: Chunk[] = [];
    for (const [side, st] of this.sides) {
      if (st.fill) out.push({ side, start: st.start, pcm: st.buf.slice(0, st.fill) });
    }
    this.sides.clear();
    return out;
  }
}

// ── HoldBack: participant chunks in a masking study (session samples) ─────

function addSpan(spans: Span[], start: number, end: number): Span[] {
  const out: Span[] = [];
  let s = start;
  let e = end;
  let placed = false;
  for (const [a, b] of spans) {
    if (b < s) out.push([a, b]);
    else if (a > e) {
      if (!placed) {
        out.push([s, e]);
        placed = true;
      }
      out.push([a, b]);
    } else {
      s = Math.min(s, a);
      e = Math.max(e, b);
    }
  }
  if (!placed) out.push([s, e]);
  return out;
}

export class HoldBack {
  private now: () => number;
  private timeoutMs: number;
  released: Span[] = [];             // sorted, merged [start, end)
  bleeps: Span[] = [];               // [start, end), zeroed wherever they land
  private held: { chunk: Chunk; closedAt: number }[] = [];

  constructor({ now = Date.now, timeoutMs = HOLD_BACK_S * 1000 }: { now?: () => number; timeoutMs?: number } = {}) {
    this.now = now;
    this.timeoutMs = timeoutMs;
  }

  release(start: number, end: number, bleep: Span[] = []): void {
    if (end > start) this.released = addSpan(this.released, start, end);
    for (const [s, e] of bleep || []) if (e > s) this.bleeps.push([s, e]);
  }

  offer(chunk: Chunk): void {
    this.held.push({ chunk, closedAt: this.now() });
  }

  private covered(start: number, end: number): boolean {
    return this.released.some(([a, b]) => a <= start && b >= end);
  }

  private scrub(chunk: Chunk, zeroUnreleased: boolean): Chunk {
    const pcm = chunk.pcm.slice();
    const s0 = chunk.start;
    const e0 = s0 + pcm.length;
    if (zeroUnreleased) {
      let cur = s0;
      for (const [a, b] of this.released) {
        if (b <= cur) continue;
        if (a >= e0) break;
        if (a > cur) pcm.fill(0, cur - s0, a - s0);
        cur = Math.max(cur, b);
      }
      if (cur < e0) pcm.fill(0, cur - s0);
    }
    for (const [a, b] of this.bleeps) {
      const x = Math.max(a, s0);
      const y = Math.min(b, e0);
      if (y > x) pcm.fill(0, x - s0, y - s0);
    }
    return { ...chunk, pcm };
  }

  /** Chunks ready to upload: fully released, or past the timeout (then with
   *  every unreleased sample zeroed). Bleep windows are zeroed in both. */
  due(nowMs = this.now()): Chunk[] {
    const out: Chunk[] = [];
    const keep: { chunk: Chunk; closedAt: number }[] = [];
    for (const h of this.held) {
      const { start, pcm } = h.chunk;
      if (this.covered(start, start + pcm.length)) out.push(this.scrub(h.chunk, false));
      else if (nowMs - h.closedAt >= this.timeoutMs) out.push(this.scrub(h.chunk, true));
      else keep.push(h);
    }
    this.held = keep;
    return out;
  }

  /** Session over: every held chunk goes out now, with every unreleased
   *  sample (and bleep window) zeroed — never raw (Ruling O). Released speech
   *  is kept, and the participant track has no gap for finalize to fill. */
  flush(): Chunk[] {
    const out = this.held.map((h) => this.scrub(h.chunk, true));
    this.held = [];
    return out;
  }

  /** The session is gone (410): nothing can be uploaded, drop it. */
  discard(): void {
    this.held = [];
  }
}

// ── Uploader: one session's PUTs (and its end POST) ───────────────────────

interface Attempt {
  res: Response | null;
  status: number;
  error: unknown;
}

async function attempt(
  fetchFn: FetchFn,
  url: string,
  init: RequestInit,
  { sleep, retry }: { sleep: (ms: number) => Promise<void>; retry: boolean }
): Promise<Attempt> {
  for (let n = 0; ; n++) {
    let res: Response | null = null;
    let error: unknown = null;
    try {
      res = await fetchFn(url, init);
    } catch (e) {
      error = e;
    }
    if (res && (res.ok || res.status < 500)) return { res, status: res.status, error: null };
    if (!retry || n >= RETRY_DELAYS_MS.length) return { res, status: res ? res.status : 0, error };
    await sleep(RETRY_DELAYS_MS[n]!);
  }
}

export interface UploaderOptions {
  apiBase: string;
  interviewId: string;
  sessionId: string;
  fetch?: FetchFn;
  onGone?: () => void;
  onError?: (e: unknown) => void;
  sleep?: (ms: number) => Promise<void>;
  headers?: HeadersFn;
}

export class Uploader {
  private base: string;
  private fetch: FetchFn;
  private onGone: () => void;
  private onError: (e: unknown) => void;
  private sleep: (ms: number) => Promise<void>;
  private headers: HeadersFn;
  gone = false;                      // 410: the session is no longer recording
  refused = false;                   // 401/403: nothing more will be accepted

  constructor(o: UploaderOptions) {
    this.base = `${o.apiBase}/interviews/${o.interviewId}/recording-sessions/${o.sessionId}`;
    this.fetch = o.fetch ?? realFetch;
    this.onGone = o.onGone ?? (() => {});
    this.onError = o.onError ?? (() => {});
    this.sleep = o.sleep ?? realSleep;
    this.headers = o.headers ?? noHeaders;
  }

  /** Resolves true once stored (a duplicate counts), false otherwise. Each
   *  upload retries 3× on 5xx or network error, 1 s / 3 s / 9 s apart; a
   *  keepalive request (the page is leaving) gets one try. The fetch itself is
   *  issued synchronously, which pagehide depends on. */
  async put(
    path: string,
    body: BodyInit | undefined,
    contentType: string,
    { method = "PUT", keepalive = false }: { method?: string; keepalive?: boolean } = {}
  ): Promise<boolean> {
    if (this.gone || this.refused) return false;
    const url = `${this.base}/${path}`;
    const init: RequestInit = { method, headers: this.headers({ "content-type": contentType }), body };
    if (keepalive) init.keepalive = true;
    const sleep = async (ms: number) => {
      await this.sleep(ms);
      if (this.gone || this.refused) throw new Error("stopped");
    };
    let r: Attempt;
    try {
      r = await attempt(this.fetch, url, init, { sleep, retry: !keepalive });
    } catch {
      return false;                  // stopped mid-retry by another upload's 410/403
    }
    if (r.res && r.res.ok) return true;
    if (r.status === 410) {
      if (!this.gone) {
        this.gone = true;
        this.onGone();
      }
      return false;
    }
    if (r.status === 401 || r.status === 403) this.refused = true;
    if (!keepalive || r.status) {
      this.onError(r.error || new Error(`recording upload ${path} failed: ${r.status}`));
    }
    return false;
  }
}

// ── RecordingSession: one server session (seq), created by Recorder ───────

export interface RecordingSessionOptions {
  apiBase: string;
  interviewId: string;
  sampleRate: number;
  origin?: number;
  masking?: boolean;
  clock?: () => number;
  onError?: (e: unknown) => void;
  onGone?: (s: RecordingSession) => void;
  fetch?: FetchFn;
  sleep?: (ms: number) => Promise<void>;
  headers?: HeadersFn;
  now?: () => number;
  timers?: Timers;
  chunkS?: number;
  holdBackS?: number;
}

type Queued = [string, BodyInit | undefined, string, number];

export class RecordingSession {
  readonly apiBase: string;
  readonly interviewId: string;
  readonly sampleRate: number;
  readonly origin: number;
  readonly masking: boolean;
  /** Where the session before this one stopped (Recorder sets it). */
  prevEnd: number | null = null;
  private clock: () => number;
  private onError: (e: unknown) => void;
  private onGone: (s: RecordingSession) => void;
  private fetch: FetchFn;
  private sleep: (ms: number) => Promise<void>;
  private headers: HeadersFn;
  private now: () => number;
  private timers: Timers;
  private chunker: Chunker;
  private holdback: HoldBack | null;
  id: string | null = null;
  seq: number | null = null;
  uploader: Uploader | null = null;
  private queue: Queued[] = [];      // uploads waiting for the session id
  private pending = new Set<Promise<boolean>>();
  private eventsN = 1;               // 0 is the server's own session line
  private eventLines: string[] = []; // timing events waiting for the next flush
  private eventTimer: unknown = null;
  stopped = false;
  failed = false;
  failStatus: number | null = null;
  private keepaliveLeft: number | null = null;   // byte budget once the page is leaving
  private ready: Promise<RecordingSession | null> | null = null;

  constructor(o: RecordingSessionOptions) {
    this.apiBase = o.apiBase;
    this.interviewId = o.interviewId;
    this.sampleRate = o.sampleRate;
    this.origin = o.origin ?? 0;
    this.masking = !!o.masking;
    const origin = this.origin;
    this.clock = o.clock ?? (() => origin);
    this.onError = o.onError ?? (() => {});
    this.onGone = o.onGone ?? (() => {});
    this.fetch = o.fetch ?? realFetch;
    this.sleep = o.sleep ?? realSleep;
    this.headers = o.headers ?? noHeaders;
    this.now = o.now ?? Date.now;
    this.timers = o.timers ?? realTimers;
    this.chunker = new Chunker(this.sampleRate, o.chunkS ?? CHUNK_S);
    this.holdback = this.masking
      ? new HoldBack({ now: this.now, timeoutMs: (o.holdBackS ?? HOLD_BACK_S) * 1000 })
      : null;
  }

  /** The one-shot form: create the server session and resolve it, or null
   *  when the server refuses. (Recorder uses begin() so audio can be fed while
   *  the POST is in flight.) */
  static async start(o: RecordingSessionOptions): Promise<RecordingSession | null> {
    return new RecordingSession(o).begin();
  }

  begin(): Promise<RecordingSession | null> {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      const r = await attempt(
        this.fetch,
        `${this.apiBase}/interviews/${this.interviewId}/recording-sessions`,
        {
          method: "POST",
          headers: this.headers({ "content-type": "application/json" }),
          body: JSON.stringify({ sample_rate: this.sampleRate, video: false, origin: this.origin }),
        },
        { sleep: this.sleep, retry: true }
      );
      if (!r.res || !r.res.ok) {
        this.failed = true;
        this.failStatus = r.status;
        this.queue = [];
        this.onError(r.error || new Error(`recording session not created: ${r.status}`));
        return null;
      }
      const body = await r.res.json();
      this.id = String(body.session_id);
      this.seq = body.seq;
      this.uploader = new Uploader({
        apiBase: this.apiBase,
        interviewId: this.interviewId,
        sessionId: this.id,
        fetch: this.fetch,
        sleep: this.sleep,
        headers: this.headers,
        onError: this.onError,
        onGone: () => this.gone(),
      });
      const queued = this.queue;
      this.queue = [];
      for (const q of queued) this.send(...q);
      return this;
    })();
    return this.ready;
  }

  private gone(): void {
    this.stopped = true;
    this.holdback?.discard();
    this.onGone(this);
  }

  private send(path: string, body: BodyInit | undefined, type: string, size: number): void {
    if (!this.uploader) return;
    let keepalive = false;
    if (this.keepaliveLeft !== null) {
      if (size > this.keepaliveLeft) return;     // won't fit; the lapse sweep has the rest
      this.keepaliveLeft -= size;
      keepalive = true;
    }
    const p = this.uploader.put(path, body, type, { keepalive });
    this.pending.add(p);
    void p.finally(() => this.pending.delete(p));
  }

  private put(path: string, body: BodyInit | undefined, type: string, size: number): void {
    if (this.failed || this.uploader?.gone) return;
    if (!this.uploader) {
      this.queue.push([path, body, type, size]);
      return;
    }
    this.send(path, body, type, size);
  }

  private uploadAudio(chunk: Chunk): void {
    const bytes = bytesOf(chunk.pcm);
    this.put(`audio/${chunk.side}/${chunk.start}`, bytes, "application/octet-stream", bytes.byteLength);
  }

  private route(chunk: Chunk): void {
    if (chunk.side === "p" && this.holdback) this.holdback.offer(chunk);
    else this.uploadAudio(chunk);
  }

  private drain(): void {
    if (!this.holdback) return;
    for (const c of this.holdback.due(this.now())) this.uploadAudio(c);
  }

  /** A timing event (stt_words, tts_words, playout) in this session's
   *  samples. Batched: one events/{n} PUT every EVENT_FLUSH_MS and at stop. */
  event(obj: unknown): void {
    if (this.stopped || this.failed) return;
    this.eventLines.push(JSON.stringify(obj) + "\n");
    if (this.eventTimer === null) {
      this.eventTimer = this.timers.setTimeout(() => {
        this.eventTimer = null;
        this.flushEvents();
      }, EVENT_FLUSH_MS);
    }
  }

  private flushEvents(): void {
    if (this.eventTimer !== null) {
      this.timers.clearTimeout(this.eventTimer);
      this.eventTimer = null;
    }
    if (!this.eventLines.length) return;
    const body = new TextEncoder().encode(this.eventLines.join(""));
    this.eventLines = [];
    this.put(`events/${this.eventsN++}`, body, "application/x-ndjson", body.byteLength);
  }

  putEvent(obj: unknown): void {
    const line = new TextEncoder().encode(JSON.stringify(obj) + "\n");
    this.put(`events/${this.eventsN++}`, line, "application/x-ndjson", line.byteLength);
  }

  /** Samples starting at session sample `sample` (Recorder converts). */
  feed(sample: number, p: Float32Array, i: Float32Array): void {
    if (this.stopped || this.failed) return;
    for (const c of this.chunker.push("p", sample, p)) this.route(c);
    for (const c of this.chunker.push("i", sample, i)) this.route(c);
    this.drain();
  }

  /** Masking only: [startSample, endSample) passed the PII check; `bleep`
   *  windows inside it are zeroed. Session samples. */
  release(startSample: number, endSample: number, bleep: Span[] = []): void {
    if (!this.holdback || this.stopped) return;
    this.holdback.release(startSample, endSample, bleep);
    this.drain();
  }

  currentSample(): number {
    return Math.max(0, this.clock() - this.origin);
  }

  async stop({ keepalive = false }: { keepalive?: boolean } = {}): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    if (keepalive) this.keepaliveLeft = KEEPALIVE_BUDGET - END_RESERVE;
    this.flushEvents();
    for (const c of this.chunker.flush()) this.route(c);
    this.drain();
    // Held participant chunks go out with unreleased samples zeroed.
    if (this.holdback) for (const c of this.holdback.flush()) this.uploadAudio(c);

    if (keepalive) {
      // pagehide: nothing after this can be awaited. Without a session id
      // (the POST still in flight) there is nothing to end.
      if (this.uploader) {
        void this.uploader.put("end", undefined, "application/json", { method: "POST", keepalive: true });
      }
      return;
    }
    if (!(await this.ready)) return;
    let timer: unknown;
    const bound = new Promise<void>((r) => {
      timer = this.timers.setTimeout(r, STOP_WAIT_MS);
    });
    await Promise.race([Promise.allSettled([...this.pending]), bound]);
    this.timers.clearTimeout(timer);
    if (!this.uploader || this.uploader.gone) return;
    await this.uploader.put("end", undefined, "application/json", { method: "POST" });
  }
}

// ── Recorder: one per interview ───────────────────────────────────────────

export interface Worklet {
  detach(): void;
}

export interface AttachOptions {
  ctx: AudioContext;
  micInput: AudioNode | null;
  ttsTap: AudioNode | null;
  workletUrl: string | null;
  onBatch(data: { frame: number; p: Float32Array; i: Float32Array; time?: number }): void;
}

/** Load the worklet and connect mic → input 0, TTS tap → input 1. The node's
 *  output goes to the destination through a zero gain so it keeps running,
 *  but nothing is heard. `micInput` is a node in `ctx` the page connects each
 *  open mic into: it reaches the worklet with no MediaStream round trip, so
 *  captureFrame() is where its samples land. */
export async function attachWorklet(o: AttachOptions): Promise<Worklet> {
  const { ctx, micInput, ttsTap } = o;
  await loadWorklet(ctx, o.workletUrl, RECORDER_WORKLET_SOURCE);
  const node = new AudioWorkletNode(ctx, "deutero-recorder", {
    numberOfInputs: 2,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 1,                 // the browser downmixes each input to mono
    channelCountMode: "explicit",
    channelInterpretation: "speakers",
  });
  node.port.onmessage = (e: MessageEvent) => o.onBatch(e.data);
  micInput?.connect(node, 0, 0);
  ttsTap?.connect(node, 0, 1);
  const sink = ctx.createGain();
  sink.gain.value = 0;
  node.connect(sink).connect(ctx.destination);
  return {
    detach() {
      try { micInput?.disconnect(node); } catch { /* already */ }
      try { ttsTap?.disconnect(node); } catch { /* already */ }
      try { node.disconnect(); sink.disconnect(); } catch { /* already */ }
      node.port.onmessage = null;
    },
  };
}

/** The parts of an AudioContext the Recorder reads (tests pass a stub). */
export interface RecorderContext {
  sampleRate: number;
  currentTime?: number;
  audioWorklet?: unknown;
  getOutputTimestamp?: () => AudioTimestamp;
}

export interface RecorderOptions {
  apiBase: string;
  interviewId: string;
  ctx: RecorderContext;
  micInput?: AudioNode | null;
  ttsTap?: AudioNode | null;
  masking?: boolean;
  /** The worklet module on the widget's own origin (null: inline only). */
  workletUrl?: string | null;
  onError?: (e: unknown) => void;
  onGone?: () => void;
  onPaused?: (paused: boolean) => void;
  idleEndS?: number;
  maxSessionS?: number;
  prerollS?: number;
  chunkS?: number;
  holdBackS?: number;
  fetch?: FetchFn;
  timers?: Timers;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  headers?: HeadersFn;
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener"> | null;
  attach?: (o: AttachOptions) => Promise<Worklet>;
}

export type EndReason = "idle" | "pagehide" | "complete" | "cap";

export class Recorder {
  readonly apiBase: string;
  readonly interviewId: string;
  readonly ctx: RecorderContext;
  readonly masking: boolean;
  readonly sampleRate: number;
  private onError: (e: unknown) => void;
  private onGone: () => void;
  private onPaused: (paused: boolean) => void;
  private idleEndS: number;
  private chunkS: number;
  private holdBackS: number;
  private fetch: FetchFn;
  private timers: Timers;
  private now: () => number;
  private sleep: (ms: number) => Promise<void>;
  private headers: HeadersFn;
  private capSamples: number;
  private ringP: Float32Array;
  private ringI: Float32Array;
  private ringPos = 0;
  private ringHeld = 0;
  private ingested = 0;              // page frame after the last sample received
  private lastEnd = 0;               // page frame where the last session stopped
  private timeBase: number | null = null;    // context time of page frame 0
  session: RecordingSession | null = null;
  private idleTimer: unknown = null;
  closed = false;                    // the interview completed
  disabled = false;                  // the server refused a session (401/403)
  private worklet: Worklet | null = null;
  private attaching: boolean;        // activity waits for the worklet
  private pendingActivity = false;
  private eventTarget: RecorderOptions["eventTarget"];
  private onPagehide = () => void this.end("pagehide");
  readonly ready: Promise<void>;

  constructor(o: RecorderOptions) {
    this.apiBase = o.apiBase;
    this.interviewId = o.interviewId;
    this.ctx = o.ctx;
    this.masking = !!o.masking;
    this.onError = o.onError ?? (() => {});
    this.onGone = o.onGone ?? (() => {});
    this.onPaused = o.onPaused ?? (() => {});
    this.idleEndS = o.idleEndS ?? RECORDING_IDLE_END_S;
    this.chunkS = o.chunkS ?? CHUNK_S;
    this.holdBackS = o.holdBackS ?? HOLD_BACK_S;
    this.fetch = o.fetch ?? realFetch;
    this.timers = o.timers ?? realTimers;
    this.now = o.now ?? Date.now;
    this.sleep = o.sleep ?? realSleep;
    this.headers = o.headers ?? noHeaders;
    this.sampleRate = o.ctx.sampleRate;
    this.capSamples = Math.round((o.maxSessionS ?? RECORDING_MAX_SESSION_S) * this.sampleRate);
    const ring = Math.max(0, Math.round((o.prerollS ?? PREROLL_S) * this.sampleRate));
    this.ringP = new Float32Array(ring);
    this.ringI = new Float32Array(ring);

    this.eventTarget = o.eventTarget === undefined ? globalThis : o.eventTarget;
    this.eventTarget?.addEventListener?.("pagehide", this.onPagehide);

    const attach = o.attach ?? attachWorklet;
    this.attaching = !!o.ctx.audioWorklet;
    this.ready = o.ctx.audioWorklet
      ? attach({
          ctx: o.ctx as AudioContext,
          micInput: o.micInput ?? null,
          ttsTap: o.ttsTap ?? null,
          workletUrl: o.workletUrl ?? null,
          onBatch: (d) => this.ingest(d.frame, d.p, d.i, d.time),
        })
          .then((w) => {
            this.worklet = w;
          })
          .catch((e) => {
            this.disabled = true;
            this.onError(e);
          })
          .finally(() => {
            this.attaching = false;
            // An activity that came while the worklet loaded starts the first
            // session now — never before, or a failed attach would leave the
            // server an empty (no_audio) session and the page saying "recording".
            if (this.pendingActivity) {
              this.pendingActivity = false;
              this.activity();
            }
          })
      : Promise.resolve();
  }

  /** OUTPUT clock — for what the participant HEARD (interviewer playout),
   *  never for mic input (use captureFrame). The output timestamp where the
   *  browser has one (not Safari), else the context's render time; before
   *  the worklet has spoken, the frames received so far. */
  pageFrame(): number {
    if (this.timeBase !== null) {
      let t: number | null = null;
      try {
        const ts = this.ctx.getOutputTimestamp?.();
        if (ts && (ts.contextTime ?? 0) > 0) t = ts.contextTime!;
      } catch {
        /* not supported */
      }
      if (t === null && typeof this.ctx.currentTime === "number") t = this.ctx.currentTime;
      if (t !== null) return Math.max(0, Math.round((t - this.timeBase) * this.sampleRate));
    }
    return this.ingested;
  }

  /** RENDER/INGEST clock — for MIC INPUT (Ruling Q): the page frame that
   *  audio entering the worklet now is stamped with; before the worklet has
   *  spoken, the frames received so far. */
  captureFrame(): number {
    if (this.timeBase !== null && typeof this.ctx.currentTime === "number") {
      return Math.max(0, Math.round((this.ctx.currentTime - this.timeBase) * this.sampleRate));
    }
    return this.ingested;
  }

  /** A batch from the worklet: `frame` is the page frame of p[0]/i[0]. */
  ingest(frame: number, p: Float32Array, i: Float32Array, time?: number): void {
    if (typeof time === "number" && this.timeBase === null) this.timeBase = time - frame / this.sampleRate;
    const n = p.length;
    let off = 0;
    // Frames from before a session's origin (a barrier put it ahead of what
    // has arrived) belong to no session.
    if (this.session && frame < this.session.origin) off = Math.min(n, this.session.origin - frame);
    while (this.session && off < n) {
      const s = this.session;
      const capEnd = s.origin + this.capSamples;
      if (frame + n <= capEnd) {
        s.feed(frame + off - s.origin, p.subarray(off), i.subarray(off));
        off = n;
      } else {
        const cut = Math.max(off, capEnd - frame);
        if (cut > off) s.feed(frame + off - s.origin, p.subarray(off, cut), i.subarray(off, cut));
        this.rollover(frame + cut);
        off = cut;
      }
    }
    this.remember(p, i);
    this.ingested = frame + n;
  }

  private remember(p: Float32Array, i: Float32Array): void {
    const size = this.ringP.length;
    if (!size) return;
    const from = Math.max(0, p.length - size);
    for (let k = from; k < p.length; k++) {
      this.ringP[this.ringPos] = p[k]!;
      this.ringI[this.ringPos] = i[k]!;
      this.ringPos = (this.ringPos + 1) % size;
    }
    this.ringHeld = Math.min(size, this.ringHeld + p.length - from);
  }

  /** The last `count` frames of a ring, oldest first. */
  private recent(ring: Float32Array, count: number): Float32Array {
    const size = ring.length;
    const out = new Float32Array(count);
    let at = (this.ringPos - count + size) % size;
    for (let k = 0; k < count; k++) {
      out[k] = ring[at]!;
      at = (at + 1) % size;
    }
    return out;
  }

  /** `announce`: an activity-started session reports onPaused(false) once the
   *  server has created it — not before, so "recording" is never shown for a
   *  session that doesn't exist. A rollover announces nothing. */
  private open(origin: number, replay: number, announce = false): RecordingSession {
    const s = new RecordingSession({
      apiBase: this.apiBase,
      interviewId: this.interviewId,
      sampleRate: this.sampleRate,
      origin,
      masking: this.masking,
      clock: () => this.pageFrame(),
      onError: this.onError,
      onGone: (gone) => this.gone(gone),
      fetch: this.fetch,
      sleep: this.sleep,
      headers: this.headers,
      now: this.now,
      timers: this.timers,
      chunkS: this.chunkS,
      holdBackS: this.holdBackS,
    });
    s.prevEnd = this.lastEnd;
    this.session = s;
    void s.begin().then((ok) => {
      if (this.session !== s) return;
      if (ok) {
        if (announce) this.onPaused(false);
        return;
      }
      this.session = null;
      this.clearIdle();
      this.onPaused(true);
      // 401/403: no effective audio, or not this participant's interview;
      // 404: the interview is gone (erased) — stop for the rest of the page.
      // Anything else (5xx, network after retries) is transient: the next
      // activity tries a new session.
      if (s.failStatus === 401 || s.failStatus === 403 || s.failStatus === 404) this.disabled = true;
    });
    if (replay > 0) s.feed(0, this.recent(this.ringP, replay), this.recent(this.ringI, replay));
    return s;
  }

  private rollover(frame: number): void {
    const old = this.session!;
    this.session = null;
    this.lastEnd = frame;
    old.stop().catch((e) => this.onError(e));
    this.open(frame, 0);
  }

  private gone(s: RecordingSession): void {
    if (this.session === s) {
      this.session = null;
      this.lastEnd = Math.max(this.lastEnd, this.ingested);
      this.clearIdle();
      this.onPaused(true);
    }
    this.onGone();
  }

  private clearIdle(): void {
    if (this.idleTimer !== null) this.timers.clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  /** Every activity signal: starts a session (seeded with the pre-roll) when
   *  there is none, and resets the idle timer either way. */
  activity(): void {
    if (this.closed || this.disabled) return;
    if (this.attaching) {
      this.pendingActivity = true;
      return;
    }
    this.clearIdle();
    this.idleTimer = this.timers.setTimeout(() => {
      this.idleTimer = null;
      void this.end("idle");
    }, this.idleEndS * 1000);
    if (this.session) return;
    // Never reach back into audio the previous session already holds.
    const origin = Math.max(this.ingested - this.ringHeld, this.lastEnd);
    this.open(origin, Math.max(0, this.ingested - origin), true);
  }

  /** Nothing captured before now may be recorded: the next session starts no
   *  earlier than the current capture frame (its pre-roll stops here too).
   *  The widget sets one when its panel reopens (Ruling T). */
  barrier(): void {
    this.lastEnd = Math.max(this.lastEnd, this.captureFrame());
  }

  end(reason: EndReason): Promise<void> {
    if (reason === "complete") this.closed = true;
    this.clearIdle();
    const s = this.session;
    if (!s) return Promise.resolve();
    this.session = null;
    this.lastEnd = Math.max(this.lastEnd, this.ingested);
    this.onPaused(true);
    return s.stop({ keepalive: reason === "pagehide" }).catch((e) => this.onError(e));
  }

  /** {session, sample} for a page frame in the live session, else null. */
  toSession(frame: number): { session: RecordingSession; sample: number } | null {
    const s = this.session;
    if (!s || frame < s.origin || frame >= s.origin + this.capSamples) return null;
    return { session: s, sample: frame - s.origin };
  }

  /** The page frame context time `ctxTime` is rendered at (the worklet's
   *  clock), or null before the worklet has spoken. For audio the page
   *  scheduled: what the TTS tap records at that time. */
  frameAt(ctxTime: number): number | null {
    if (this.timeBase === null || typeof ctxTime !== "number") return null;
    return Math.max(0, Math.round((ctxTime - this.timeBase) * this.sampleRate));
  }

  /** A word-timing event in page frames (`span` and each word's start and
   *  end) → {session, event} in that session's samples, or null when the span
   *  lies in no live session or straddles two. Word times are clamped to the
   *  span. */
  toSessionEvent(msg: any): { session: RecordingSession; event: any } | null {
    const span = msg?.span;
    if (!Array.isArray(span) || span.length !== 2 || !(span[1] >= span[0])) return null;
    const a = this.toSession(span[0]);
    const b = this.toSession(Math.max(span[0], span[1] - 1));
    if (!a || !b || a.session !== b.session) return null;
    const s = a.session;
    if (span[0] < (s.prevEnd ?? s.origin)) return null;
    const lo = span[0] - s.origin;
    const hi = span[1] - s.origin;
    const at = (f: number) => Math.min(hi, Math.max(lo, f - s.origin));
    const words = (Array.isArray(msg.words) ? msg.words : []).map(
      ([text, start, end, ...rest]: any[]) => [text, at(start), at(end), ...rest]
    );
    return { session: s, event: { ...msg, span: [lo, hi], words } };
  }

  /** Masking: page frames [startFrame, endFrame) released, `bleep` page-frame
   *  windows zeroed. A start before the live session's origin is clamped to
   *  it when those frames belong to no session (the gap after an idle end); a
   *  span reaching into an earlier session, or past this one's cap, straddles
   *  two and is dropped (false). */
  release(startFrame: number, endFrame: number, bleep: Span[] = []): boolean {
    if (!this.masking || !(endFrame >= startFrame)) return false;
    const s = this.session;
    if (!s || endFrame <= s.origin) return false;
    if (startFrame < (s.prevEnd ?? s.origin)) return false;
    if (endFrame > s.origin + this.capSamples) return false;
    const start = Math.max(startFrame, s.origin);
    s.release(
      start - s.origin,
      endFrame - s.origin,
      (bleep || []).map(([x, y]) => [x - s.origin, y - s.origin] as Span)
    );
    return true;
  }

  /** Teardown: end the session and let the worklet go. */
  dispose(reason: EndReason = "complete"): Promise<void> {
    const done = this.end(reason);
    this.eventTarget?.removeEventListener?.("pagehide", this.onPagehide);
    this.worklet?.detach();
    this.worklet = null;
    return done;
  }
}
